import { HlsConfig } from 'hls.js';

// https://github.com/video-dev/hls.js/blob/master/docs/API.md

const hlsConfig: Partial<HlsConfig> = {
    /** __________________________
     * EDGE OF LIVE OPTIONS
     * ___________________________ */

    // Edge of live delay, how many segments late you should be
    liveSyncDurationCount: 2, // default: 3 segments

    // Maximum delay allowed from edge of live in segments
    liveMaxLatencyDurationCount: 4, // default: Infinity

    // Number of segments needed to start a playback of Live stream. Buffering will begin after N chunks are available in the current playlist.
    // If you want playback to begin liveSyncDurationCount chunks from the live edge at the beginning of a stream, set initialLiveManifestSize to liveSyncDurationCount or higher.
    initialLiveManifestSize: 1, // default: 1 segment

    // When set to a value greater than 1, the latency-controller will adjust video.playbackRate up to maxLiveSyncPlaybackRate to catch up to target latency in a live stream
    maxLiveSyncPlaybackRate: 1.0, // default: 1.0, min 1.0 max 2.0

    // If you want to have a native Live UI in environments like iOS Safari, Safari, Android Google Chrome, etc. set this value to true.
    liveDurationInfinity: false, // default: false

    /** __________________________
     * BUFFER OPTIONS
     * ___________________________ */

    // The maximum duration of buffered media to keep once it has been played, in seconds
    backBufferLength: 0, // default: Infinity

    // If buffer length is/become less than this value, a new fragment will be loaded
    maxBufferLength: 30, // default: 30s

    // If buffer size upfront is bigger than this value in Bytes, no fragment will be loaded.
    // This is bypassed by maxBufferLength that guarantees that at least 30s are buffered upfront,
    // therefore we can put 0 without preventing the prefetch of segments
    maxBufferSize: 0, // default: 60 * 1000 * 1000

    // 'Maximum' inter-fragment buffer hole tolerance that hls.js can cope with when searching for the next fragment to load.
    // When switching between quality level, fragments might not be perfectly aligned. This could result in small overlapping or hole in media buffer.
    maxBufferHole: 0.5, // default: 0.5s

    // if media element is expected to play and if currentTime has not moved for more than highBufferWatchdogPeriod and if there are more than maxBufferHole seconds buffered upfront,
    // hls.js will jump buffer gaps, or try to nudge playhead to recover playback
    highBufferWatchdogPeriod: 3, // default: 3s

    // In case playback continues to stall after first playhead nudging, currentTime will be nudged even more following nudgeOffset to try to restore playback.
    // media.currentTime += (nb nudge retry -1)*nudgeOffset
    nudgeOffset: 0.1, // default: 0.1s

    // Max nb of nudge retries before hls.js raise a fatal BUFFER_STALLED_ERROR
    nudgeMaxRetry: 3, // default: 3

    // If buffered.end = 9.991, should download fragment <10 or the one that starts at 10 ? I guess it should not be > than maxBufferHole
    maxFragLookUpTolerance: 0.25, // default: 0.25s

    // Hls.js will never exceed this value
    maxMaxBufferLength: 600, // default: 600s

    // Start prefetching start fragment although media not attached yet.
    startFragPrefetch: false, // default: false

    // If a segment's video track is shorter than its audio track by > maxBufferHole, extend the final video frame's duration to match the audio track's duration.
    // This helps playback continue in certain cases that might otherwise get stuck.
    stretchShortVideoTrack: false, // default: false

    /** __________________________
     * LEVEL OPTIONS
     * ___________________________ */

    // Start level index (level of first fragment that will be played back), if undefined -> first on manifest, if -1 automatic based on bandwidth measured on lowest level
    startLevel: undefined, // default: undefined

    // You must also set startLevel = -1 for this to have any impact. Otherwise, hls.js will load the first level in the manifest and start playback from there.
    // If you do set startLevel = -1, a fragment of the lowest level will be downloaded to establish a bandwidth estimate before selecting the first auto-level.
    // Disable this test if you'd like to provide your own estimate or use the default abrEwmaDefaultEstimate.
    testBandwidth: true, // default: true

    // Do not display a video with a higher resolution than the player size
    capLevelToPlayerSize: true, // default: false
    // calculations related to player size will ignore browser devicePixelRatio
    ignoreDevicePixelRatio: false, // default: false

    // If the number of dropped frames over the period fpsDroppedMonitoringPeriod exceeds the ratio set by fpsDroppedMonitoringThreshold,
    // then the quality level is dropped and capped at this lower level
    capLevelOnFPSDrop: false, // default: false
    fpsDroppedMonitoringPeriod: 5000, // default: 5000
    fpsDroppedMonitoringThreshold: 0.2, // default: 0.2

    // Max allowed delay to download a segment before lowering level
    maxLoadingDelay: 4, // default: 4s

    // ABR algorithm will try to find a level that should guarantee less than maxStarvationDelay of buffering
    maxStarvationDelay: 4, // default: 4s

    // Enable Low-Latency HLS part playlist and segment loading, and start live streams at playlist PART-HOLD-BACK rather than HOLD-BACK.
    lowLatencyMode: true, // default: true

    /** __________________________
     * ADAPTIVE BITRATE OPTIONS
     * ___________________________ */

    // Use the average measured bitrate instead of the manifest's
    abrMaxWithRealBitrate: false, // default: false

    // Default bandwidth estimate in bits/s prior to collecting fragment bandwidth samples.
    abrEwmaDefaultEstimate: 5e5, // default: 5e5

    // Capping/min bandwidth value that could be used by automatic level selection algorithm.
    // Useful when browser or tab of the browser is not in the focus and bandwidth drops
    minAutoBitrate: 0, // default: 0

    // Scale factor to be applied against measured bandwidth average, to determine whether we can stay on current or lower quality level.
    // If abrBandWidthFactor * bandwidth average > level.bitrate then ABR can switch to that level providing that it is equal or less than current level.
    abrBandWidthFactor: 0.95, // default: 0.95

    // Scale factor to be applied against measured bandwidth average, to determine whether we can switch up to a higher quality level.
    // If abrBandWidthUpFactor * bandwidth average > level.bitrate then ABR can switch up to that quality level.
    abrBandWidthUpFactor: 0.7, // default: 0.7

    // Fast bitrate Exponential moving average half-life, used to compute average bitrate for Live streams.
    // Half of the estimate is based on the last abrEwmaFastLive seconds of sample history.
    // Each of the sample is weighted by the fragment loading duration.
    abrEwmaFastLive: 3.0, // default: 3.0
    abrEwmaFastVoD: 3.0, // default: 3.0

    // Slow bitrate Exponential moving average half-life, used to compute average bitrate for Live streams.
    // Half of the estimate is based on the last abrEwmaSlowLive seconds of sample history.
    // Each of the sample is weighted by the fragment loading duration.
    abrEwmaSlowLive: 9.0, // default: 9.0
    abrEwmaSlowVoD: 9.0, // default: 9.0

    /** __________________________
     * LOAD POLICIES
     * ___________________________ */

    manifestLoadPolicy: {
        default: {
            maxTimeToFirstByteMs: Infinity,
            maxLoadTimeMs: 20000,
            timeoutRetry: {
                maxNumRetry: 2,
                retryDelayMs: 0,
                maxRetryDelayMs: 0,
            },
            errorRetry: {
                maxNumRetry: 1,
                retryDelayMs: 1000,
                maxRetryDelayMs: 8000,
            },
        },
    },
    playlistLoadPolicy: {
        default: {
            maxTimeToFirstByteMs: 10000,
            maxLoadTimeMs: 20000,
            timeoutRetry: {
                maxNumRetry: 2,
                retryDelayMs: 0,
                maxRetryDelayMs: 0,
            },
            errorRetry: {
                maxNumRetry: 2,
                retryDelayMs: 1000,
                maxRetryDelayMs: 8000,
            },
        },
    },
    fragLoadPolicy: {
        default: {
            maxTimeToFirstByteMs: 10000,
            maxLoadTimeMs: 120000,
            timeoutRetry: {
                maxNumRetry: 4,
                retryDelayMs: 0,
                maxRetryDelayMs: 0,
            },
            errorRetry: {
                maxNumRetry: 6,
                retryDelayMs: 1000,
                maxRetryDelayMs: 8000,
            },
        },
    },
    keyLoadPolicy: {
        default: {
            maxTimeToFirstByteMs: 8000,
            maxLoadTimeMs: 20000,
            timeoutRetry: {
                maxNumRetry: 1,
                retryDelayMs: 1000,
                maxRetryDelayMs: 20000,
                backoff: 'linear',
            },
            errorRetry: {
                maxNumRetry: 8,
                retryDelayMs: 1000,
                maxRetryDelayMs: 20000,
                backoff: 'linear',
            },
        },
    },
    certLoadPolicy: {
        default: {
            maxTimeToFirstByteMs: 8000,
            maxLoadTimeMs: 20000,
            timeoutRetry: null,
            errorRetry: null,
        },
    },
    steeringManifestLoadPolicy: {
        default: {
            maxTimeToFirstByteMs: 10000,
            maxLoadTimeMs: 20000,
            timeoutRetry: {
                maxNumRetry: 2,
                retryDelayMs: 0,
                maxRetryDelayMs: 0,
            },
            errorRetry: {
                maxNumRetry: 1,
                retryDelayMs: 1000,
                maxRetryDelayMs: 8000,
            },
        }
    }

};

export default hlsConfig;
